import { useIsInterruptedRemoteSession } from '@/hooks/desktop/useIsInterruptedRemoteSession';
import { useIsLiveOfflineRemoteSession } from '@/hooks/desktop/useIsLiveOfflineRemoteSession';
import { useIsLiveRunningRemoteSession } from '@/hooks/desktop/useIsLiveRunningRemoteSession';
import { useIsPureOfflineRemoteSession } from '@/hooks/desktop/useIsPureOfflineRemoteSession';
import { useIsRemoteReconnectedSession } from '@/hooks/desktop/useIsRemoteReconnectedSession';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { AlertTriangle } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

interface InterruptedSessionIconProps {
  sessionId: string | null | undefined;
}

/**
 * Interrupted Remote warning for the V1 sidebar. Yields to offline /
 * reconnected / live-running / pure-offline (same slot as the DataTable cell).
 */
export function InterruptedSessionIcon({ sessionId }: InterruptedSessionIconProps) {
  const isRecovering = useIsInterruptedRemoteSession(sessionId);
  const isLiveOffline = useIsLiveOfflineRemoteSession(sessionId);
  const isReconnectedPendingEnd = useIsRemoteReconnectedSession(sessionId);
  const isLiveRunning = useIsLiveRunningRemoteSession(sessionId);
  const isPureOffline = useIsPureOfflineRemoteSession(sessionId);

  if (
    isLiveOffline ||
    isReconnectedPendingEnd ||
    isLiveRunning ||
    isPureOffline ||
    !isRecovering
  ) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex shrink-0 items-center"
            data-testid="sidebar-interrupted-session-icon"
          >
            <AlertTriangle className="size-3 text-validation-warning" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <FormattedMessage defaultMessage="Session Interrupted" id="c/6ewElYvI" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
